import {
  Component,
  DoCheck,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { DropdownNavComponent } from '../dropdown-nav/dropdown-nav.component';
import { ButtonComponent } from '../button/button.component';
import { FlyoutComponent } from '../flyout/flyout.component';
import { BurgerMenuComponent } from '../burger-menu/burger-menu.component';
import MenuItem, {
  FlyoutData,
} from '../../../shared/interfaces/menuItems.interface';
import { Sizes } from '../../../shared/enums/shared.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import { NavigationPage } from '../../../shared/interfaces/navigation-page.interface';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { FlyoutService } from '../../../services/flyout.service';
import { GlobalSearchType } from '../../../shared/enums/global-search-type.enums';
import { MenuService } from '../../../services/menu.service';
import { HrefType } from '../../../shared/enums/href-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { LinkComponent } from '../../atoms/link/link.component';

@Component({
  selector: 'hdi-top-bar',
  standalone: true,
  imports: [
    LogoComponent,
    CommonModule,
    RouterModule,
    IconComponent,
    DropdownNavComponent,
    ButtonComponent,
    FlyoutComponent,
    BurgerMenuComponent,
    LinkComponent,
  ],
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
})
export class TopBarComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
  @ViewChild('burger') burger!: ElementRef;
  @Input() mainMenu: Array<MenuItem> = [];
  @Input() metaMenu: Array<MenuItem> = [];
  @Input() flyoutData: FlyoutData[] = [
    {
      title: '',
      url: '',
      subpages: [],
    },
  ];

  public readonly Sizes = Sizes;
  public readonly ButtonType = ButtonType;
  public readonly ViewportType = ViewportType;
  public readonly GlobalSearchType = GlobalSearchType;
  readonly HrefType = HrefType;
  readonly LinkType = LinkType;

  public routeSubscription$!: Subscription;
  public isMenuOpen: boolean = false;
  public isFlyoutOpen: boolean = false;
  public slideUp: boolean = false;
  public currentUrl: string = '';
  public globalSearchOpen: boolean = false;
  public currentFlyout: Array<NavigationPage> = [];
  public flyoutOverview: { title: string; url: string; isSimple: boolean } = {
    title: '',
    url: '',
    isSimple: true,
  };
  public dropDownMenu: Array<MenuItem> = [];
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private flyoutService: FlyoutService,
    private renderer: Renderer2,
    private menuService: MenuService
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
      });
    this.menuService.setMainMenu(this.mainMenu);
  }

  ngOnChanges(): void {
    this.dropDownMenu = [...this.mainMenu, ...this.metaMenu];
  }

  ngDoCheck(): void {
    const inpageNavigation = this.document.querySelector(
      '.inpage-navigation-sticky'
    );
    this.slideUp = !!(
      inpageNavigation &&
      inpageNavigation.getBoundingClientRect().top <= 60 &&
      inpageNavigation.getBoundingClientRect().bottom >= 0
    );
  }

  closeMenu(): void {
    this.isMenuOpen = false;
    this.toggleScrollLock(false);
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.toggleScrollLock(this.isMenuOpen);
  }

  toggleScrollLock(isBlocked: boolean): void {
    const body = this.document.querySelector('body');
    if (body) {
      if (isBlocked) {
        this.renderer.setStyle(body, 'overflow', 'hidden');
      } else {
        this.renderer.removeStyle(body, 'overflow');
      }
    }
  }

  openFlyout(item: MenuItem): void {
    this.isFlyoutOpen = false;
    setTimeout(() => {
      this.isFlyoutOpen = true;
      // Dynamically assign flyout data based on the item properties
      const currentFlyoutData = this.flyoutData.find(
        (data) => data.title === item.title
      );
      if (currentFlyoutData) {
        this.currentFlyout = currentFlyoutData.subpages;
        this.flyoutOverview = {
          title: currentFlyoutData.title,
          url: currentFlyoutData.url,
          isSimple: true,
        };
        this.flyoutService.openFlyout();
      } else {
        this.closeFlyout();
        this.flyoutOverview = { title: '', url: '', isSimple: true };
        this.router.navigate([item.url]);
      }
    }, 0);
  }

  closeFlyout(): void {
    this.isFlyoutOpen = false;
  }

  navigateToHomepage(): void {
    this.isFlyoutOpen = false;
    this.router.navigate(['/']);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleGlobalSearch(): void {
    this.globalSearchOpen = !this.globalSearchOpen;
    if (this.globalSearchOpen) {
      onscroll = () => {
        this.globalSearchOpen = false;
      };
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
